"use client";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";

const PageHeader = ({ label, title, description }: any) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const maxScroll = 150;
      const progress = Math.min(1, scrollY / maxScroll);
      setScrollProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate dynamic values based on scroll
  // Start with more padding on mobile to account for fixed header (h-16 = 64px)
  const paddingTop = 112 - (scrollProgress * 48); // 112px -> 64px
  const paddingBottom = 56 - (scrollProgress * 24); // 56px -> 32px
  const titleScale = 1 - (scrollProgress * 0.06); // 1 -> 0.94
  const descriptionOpacity = 1 - (scrollProgress * 0.4); // 1 -> 0.6
  const labelOpacity = 1 - (scrollProgress * 1); // 1 -> 0

  return (
    <section
      className="section-gradient relative overflow-hidden transition-all duration-180 ease-out"
      style={{
        paddingTop: `${paddingTop}px`,
        paddingBottom: `${paddingBottom}px`
      }}
    >
      {/* S3 Gradient Wash Layer */}
      <div className="section-gradient-layer" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mx-auto text-center"
        >
          {/* Page Label */}
          <p
            className="text-[12px] uppercase tracking-[0.15em] text-[hsl(var(--accent))] mb-6 sm:mb-8 transition-opacity duration-180"
            style={{ fontWeight: 500, opacity: labelOpacity }}
          >
            {label}
          </p>

          {/* Page Title */}
          <h1
            className="text-[28px] sm:text-[36px] md:text-[48px] text-[hsl(var(--text))] mb-5 transition-transform duration-180 origin-center"
            style={{
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              transform: `scale(${titleScale})`
            }}
          >
            {title}
          </h1>

          {/* Supporting Description */}
          <p
            className="text-[17px] md:text-[19px] text-[hsl(var(--subtext))] transition-opacity duration-180"
            style={{
              fontWeight: 400,
              lineHeight: 1.6,
              opacity: descriptionOpacity
            }}
          >
            {description}
          </p>
        </motion.div>
      </div>

      {/* Bottom accent divider */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="accent-divider" />
      </div>
    </section>
  );
};

export default PageHeader;
